import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  lat?: number;
  lon?: number;
}

const AWS_REGION = process.env.REACT_APP_AWS_LOCATION_REGION || 'us-east-1';
const AWS_INDEX = process.env.REACT_APP_AWS_LOCATION_INDEX || 'intrlock-places';
const AWS_KEY = process.env.REACT_APP_AWS_LOCATION_KEY || '';

const STATE_ABBR: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
  'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA',
  'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN',
  texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
  'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

interface Suggestion {
  Text: string;
  PlaceId: string;
}

interface PlaceResult {
  Place: {
    Label?: string;
    AddressNumber?: string;
    Street?: string;
    Municipality?: string;
    Region?: string;
    SubRegion?: string;
    PostalCode?: string;
    Country?: string;
    Geometry?: { Point?: [number, number] };
  };
}

const parsePlace = (p: PlaceResult): ParsedAddress => {
  const a = p.Place;
  const street = [a.AddressNumber, a.Street].filter(Boolean).join(' ');
  const stateRaw = (a.Region || '').trim();
  const state = stateRaw.length === 2 ? stateRaw.toUpperCase() : (STATE_ABBR[stateRaw.toLowerCase()] || stateRaw);
  return {
    street,
    city: a.Municipality || a.SubRegion || '',
    state,
    zip: a.PostalCode || '',
    country: a.Country || '',
    lon: a.Geometry?.Point?.[0],
    lat: a.Geometry?.Point?.[1],
  };
};

interface Props {
  label?: string;
  value: string;
  onChange: (street: string) => void;
  onSelect: (parsed: ParsedAddress) => void;
  placeholder?: string;
  error?: string;
  className?: string;
  countryCode?: string; // e.g. "USA"
}

export const AddressAutocomplete: React.FC<Props> = ({
  label = 'Address',
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address...',
  error,
  className = '',
  countryCode = 'USA',
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lastQueryRef = useRef('');

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    if (!AWS_KEY) {
      console.warn('[AddressAutocomplete] missing REACT_APP_AWS_LOCATION_KEY');
      return;
    }
    setLoading(true);
    try {
      const url = `https://places.geo.${AWS_REGION}.amazonaws.com/places/v0/indexes/${AWS_INDEX}/search/suggestions?key=${encodeURIComponent(AWS_KEY)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Text: q, MaxResults: 6, FilterCountries: [countryCode] }),
      });
      if (!res.ok) throw new Error(`lookup failed ${res.status} ${await res.text().catch(() => '')}`);
      const data = await res.json();
      if (lastQueryRef.current === q) {
        setSuggestions(data.Results || []);
        setOpen(true);
        setHighlight(0);
      }
    } catch (err) {
      console.warn('[AddressAutocomplete] lookup failed', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    lastQueryRef.current = v;
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const handlePick = async (s: Suggestion) => {
    setOpen(false);
    setSuggestions([]);
    try {
      const url = `https://places.geo.${AWS_REGION}.amazonaws.com/places/v0/indexes/${AWS_INDEX}/places/${encodeURIComponent(s.PlaceId)}?key=${encodeURIComponent(AWS_KEY)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('place fetch failed');
      const place: PlaceResult = await res.json();
      const parsed = parsePlace(place);
      onChange(parsed.street || s.Text);
      onSelect(parsed);
    } catch (err) {
      console.warn('[AddressAutocomplete] place fetch failed', err);
      // Fallback: parse the suggestion text — "123 Main St, City, ST, 12345, USA"
      const parts = s.Text.split(',').map((p) => p.trim());
      const parsed: ParsedAddress = {
        street: parts[0] || '',
        city: parts[1] || '',
        state: (parts[2] || '').toUpperCase(),
        zip: parts[3] || '',
        country: parts[4] || '',
      };
      onChange(parsed.street);
      onSelect(parsed);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); handlePick(suggestions[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  useEffect(() => {
    const onClickOut = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  return (
    <div className={`mb-4 ${className}`} ref={wrapRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKey}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="street-address"
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full" />
          </div>
        )}
        {open && (loading || suggestions.length > 0 || (value.trim().length >= 3 && !loading)) && (
          <ul className="absolute z-[100] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-auto">
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-500 italic">Searching…</li>
            )}
            {!loading && suggestions.length === 0 && value.trim().length >= 3 && (
              <li className="px-3 py-2 text-sm text-gray-500 italic">No matches found</li>
            )}
            {suggestions.map((s, idx) => (
              <li
                key={s.PlaceId}
                onClick={() => handlePick(s)}
                onMouseEnter={() => setHighlight(idx)}
                className={`px-3 py-2 text-sm cursor-pointer ${idx === highlight ? 'bg-brand-50 text-brand-900' : 'hover:bg-gray-50 text-gray-700'}`}
              >
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="leading-snug">{s.Text}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
